import { redirect, type LoaderFunctionArgs } from 'react-router-dom';
import type { AxiosError } from 'axios';
import { msalInstance, loginRequest, initializeMsal } from '~/util/msalConfig';
import { queryClient } from '~/queryClient';
import { getLoggedInUserOptions, type UserViewModel } from '@generated';

export const SECURE_ROUTE_ID = 'secure-root';

export interface SecureRouteData {
	loggedInUser: UserViewModel;
}

/**
 * Ensures MSAL is initialized and an account is present. If no account exists, remembers
 * the destination and triggers a full-page login redirect (the returned promise never
 * resolves). Shared by the secure subtree loader and the /register loader.
 *
 * createBrowserRouter runs the initial loader eagerly at router-creation time — before
 * App's effect calls initializeMsal — so we must await initialization here (it's cached,
 * so this shares App's single init) before touching any MSAL API.
 */
async function ensureMsalAccount(request: Request): Promise<void> {
	await initializeMsal();

	const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];

	if (!account) {
		const url = new URL(request.url);
		sessionStorage.setItem('redirectUri', url.pathname + url.search);
		await msalInstance.loginRedirect(loginRequest);
	}
}

function isNotFound(error: unknown): boolean {
	const status = (error as AxiosError)?.status ?? (error as AxiosError)?.response?.status;
	return status === 404;
}

/**
 * Loader for the authenticated route subtree. It gates auth and resolves the logged-in
 * user BEFORE the protected shell renders, so consumers read the user from loader data
 * (useRouteLoaderData) instead of a useQuery with a loading state.
 *
 * A 404 means the MSAL-authenticated user has no Duro record yet → send them to the
 * standalone /register route, which lives outside this shell so nothing here ever renders
 * without a user.
 */
export async function secureRouteLoader({ request }: LoaderFunctionArgs): Promise<null | Response> {
	await ensureMsalAccount(request);

	try {
		await queryClient.ensureQueryData(getLoggedInUserOptions());
		return null;
	} catch (error) {
		if (isNotFound(error)) {
			return redirect('/register');
		}
		throw error;
	}
}

/**
 * Loader for the standalone /register route. Requires an authenticated MSAL account but
 * NOT a Duro user record. If the user already has a record, sends them into the app so a
 * registered user can't land on the registration screen.
 */
export async function registerRouteLoader({ request }: LoaderFunctionArgs): Promise<Response | null> {
	await ensureMsalAccount(request);

	try {
		await queryClient.ensureQueryData(getLoggedInUserOptions());
		return redirect('/');
	} catch (error) {
		if (isNotFound(error)) {
			return null;
		}
		throw error;
	}
}
